import {
  API_FightResults,
  API_GetBalance,
  API_GetFreeNft,
  API_GetOrCreateUser,
  API_GetOwnNftResponse,
  API_GetTxsHistory,
  API_GetUnfinishedFight,
  API_Login,
  API_NftDetails,
  API_NftStatusResponse,
  API_TradeCream,
  API_UpdateTxStatus,
  encodeArray,
} from 'shared';
import axios from 'axios';

type Response<T> = { data: T };

abstract class APIFetcher {
  protected sendGetRequest = <T>(url: string, params?: any): Promise<T> => {
    return new Promise((resolve, reject) => {
      axios
        .get(url, { params })
        .then((response: Response<T>) => resolve(response.data))
        .catch((err) => reject(err));
    });
  };

  protected sendPostRequest = <T>(url: string, payload?: any): Promise<T> => {
    return new Promise((resolve, reject) => {
      axios
        .post(url, payload)
        .then((response: Response<T>) => resolve(response.data))
        .catch((err) => reject(err));
    });
  };
}

class NFTFetcher extends APIFetcher {
  public getDetails = (nftId: string): Promise<API_NftDetails> => {
    return this.sendGetRequest<API_NftDetails>('/api/nft/details', { nftId });
  };

  public mint = (hash: string, nftToken: string): Promise<void> => {
    const payload = {
      transactionHash: hash,
      nftToken,
    };
    return this.sendPostRequest<void>('/api/nft/mint', payload);
  };

  public updateStatus = (token: string): Promise<API_NftStatusResponse> => {
    return this.sendPostRequest<API_NftStatusResponse>(
      '/api/nft/updatestatus',
      { token }
    );
  };

  public getOwn = (): Promise<API_GetOwnNftResponse> => {
    return this.sendGetRequest<API_GetOwnNftResponse>('/api/nft/getown');
  };

  public removeFailed = (token: string): Promise<void> => {
    return this.sendPostRequest<void>('/api/nft/removefailed', { token });
  };
}

class CreamFetcher extends APIFetcher {
  public getBalance = (): Promise<API_GetBalance> => {
    return this.sendGetRequest<API_GetBalance>('/api/cream/balance');
  };

  public getPendingTxs = (): Promise<API_GetTxsHistory> => {
    return this.sendGetRequest<API_GetTxsHistory>(
      '/api/cream/transactionshistory'
    );
  };

  public buy = (
    txHash: string,
    creamAmount: number
  ): Promise<API_TradeCream> => {
    const payload = {
      transactionHash: txHash,
      creamAmount,
    };
    return this.sendPostRequest<API_TradeCream>('/api/cream/buycream', payload);
  };

  public sell = (toSell: number): Promise<API_TradeCream> => {
    const payload = { creamAmount: toSell };
    return this.sendPostRequest<API_TradeCream>(
      '/api/cream/sellcream',
      payload
    );
  };

  public updateTxStatus = (txHash: string): Promise<API_UpdateTxStatus> => {
    const payload = { transactionHash: txHash };
    return this.sendPostRequest<API_UpdateTxStatus>(
      '/api/cream/updatetransactionstatus',
      payload
    );
  };
}

class FightFetcher extends APIFetcher {
  public getUnfinished = () => {
    return this.sendGetRequest<API_GetUnfinishedFight>('/api/fight/unfinished');
  };

  public getResults = (fightId: string) => {
    return this.sendGetRequest<API_FightResults>('/api/fight/results', {
      fightId,
    });
  };
}

class MarketFetcher extends APIFetcher {
  public getFreeNft = (exclude?: string[], exact?: string) => {
    const payload = {
      exact,
      exclude: exclude ? encodeArray(exclude) : undefined,
    };
    return this.sendPostRequest<API_GetFreeNft>(
      '/api/market/getfreenft',
      payload
    );
  };
}

class UserDataFetcher extends APIFetcher {
  public validateToken = (token: string) => {
    return this.sendGetRequest('/api/users/validateToken', {
      token,
    });
  };

  public checkAddress = (address: string) => {
    return this.sendPostRequest<API_GetOrCreateUser>('/api/users/getorcreate', {
      publicAddress: address,
    });
  };

  public login = (address: string, signature: string) => {
    const payload = {
      publicAddress: address,
      signature,
    };
    return this.sendPostRequest<API_Login>('/api/users/login', payload);
  };

  public logout = () => {
    return this.sendPostRequest('/api/users/logout');
  };
}

export class Fetcher {
  private readonly _nft: NFTFetcher;
  private readonly _cream: CreamFetcher;
  private readonly _fight: FightFetcher;
  private readonly _market: MarketFetcher;
  private readonly _user: UserDataFetcher;

  constructor() {
    this._nft = new NFTFetcher();
    this._cream = new CreamFetcher();
    this._fight = new FightFetcher();
    this._market = new MarketFetcher();
    this._user = new UserDataFetcher();
  }

  public static setTokenHeader = (token: string) => {
    axios.defaults.headers.common['x-auth'] = token;
  };

  public static clearHeaders = () => {
    axios.defaults.headers.common['x-auth'] = '';
  };

  get cream(): CreamFetcher {
    return this._cream;
  }

  get nft(): NFTFetcher {
    return this._nft;
  }

  get fight(): FightFetcher {
    return this._fight;
  }

  get market(): MarketFetcher {
    return this._market;
  }

  get user(): UserDataFetcher {
    return this._user;
  }
}

const fetcher = new Fetcher();

export default fetcher;
